/**
 * About Page Component - Collapsible Section
 * All content is in DOM for SEO/AdSense but starts collapsed for clean UI
 */

import { useState, useEffect } from 'react'
import { BookOpen, Target, Lightbulb, Shield, Scale, DollarSign, Mail, ChevronDown, ChevronUp, Heart, TrendingUp } from 'lucide-react'
import '../aboutPage.css'

const AboutPage = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Auto-expand if URL hash matches or if scrolled to from footer
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#about') {
        setIsExpanded(true)
      }
    }

    // Check on mount
    handleHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)
    
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
    
    // Scroll to section when expanding
    if (!isExpanded) {
      setTimeout(() => {
        const element = document.getElementById('about')
        if (element) {
          const offset = 80
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }

  return (
    <section className="about-page-section" id="about">
      <div className="about-container">
        
        {/* Collapsible Header - Always Visible */}
        <div className="about-header-collapsible" onClick={toggleExpanded}>
          <div className="about-header-content">
            <BookOpen size={32} color="#ff6b35" />
            <div className="header-text">
              <h2>About Zero Debt Journey</h2>
              <p className="tagline">Empowering your path to financial freedom</p>
            </div>
          </div>
          <button 
            className="toggle-button"
            aria-label={isExpanded ? 'Collapse about section' : 'Expand about section'}
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>

        {/* Summary Preview - Always Visible */}
        <div className="about-summary-preview">
          <p className="summary-preview-text">
            A free, privacy-focused debt calculator helping you visualize and plan 
            your journey to becoming debt-free. Click to learn more about our mission.
          </p>
        </div>

        {/* Expandable Content */}
        <div className={`about-content-expandable ${isExpanded ? 'expanded' : 'collapsed'}`}>
          
          <div className="about-content">
            
            {/* Section 1: Our Mission */}
            <article className="about-section mission-section">
              <div className="section-header">
                <Target size={28} color="#ff6b35" />
                <h3>Our Mission</h3>
              </div>
              <p className="mission-text">
                Zero Debt Journey was created to help everyday people take control of their 
                financial future. We believe that understanding your debt payoff timeline 
                shouldn't require expensive software or financial advisors. Our free calculator 
                empowers you with the knowledge you need to make informed decisions about your 
                debt repayment strategy.
              </p>
              <div className="mission-highlights">
                <div className="highlight-item">
                  <Heart size={20} color="#10b981" />
                  <span>Completely free to use</span>
                </div>
                <div className="highlight-item">
                  <Shield size={20} color="#3b82f6" />
                  <span>Privacy-first approach</span>
                </div>
                <div className="highlight-item">
                  <TrendingUp size={20} color="#ff6b35" />
                  <span>Educational resources</span>
                </div>
              </div>
            </article>

            {/* Section 2: Why We Built This */}
            <article className="about-section">
              <div className="section-header">
                <Lightbulb size={28} color="#fbbf24" />
                <h3>Why We Built This</h3>
              </div>
              <p>
                We've seen firsthand how overwhelming and stressful managing debt can be. Without 
                the right tools, it's difficult to visualize how your payments impact your timeline 
                or understand the true cost of carrying debt. Many people feel stuck, unsure of 
                where to start or how to make progress.
              </p>
              <p>
                That's why we created Zero Debt Journey—to provide an easy, accessible tool that 
                helps people see their path forward. We wanted to remove the mystery from debt 
                repayment and show you exactly how small changes can make a massive difference. 
                Whether it's paying an extra $50 per month or understanding the snowball vs. 
                avalanche method, knowledge is power.
              </p>
              <div className="info-callout">
                <p>
                  <strong>Did you know?</strong> Paying just $50 extra per month on a $5,000 debt 
                  at 18% APR can save you over $1,200 in interest and help you pay it off 2 years 
                  faster. That's the kind of insight our calculator provides instantly.
                </p>
              </div>
            </article>

            {/* Section 3: Our Commitment to Privacy */}
            <article className="about-section privacy-commitment-section">
              <div className="section-header">
                <Shield size={28} color="#3b82f6" />
                <h3>Our Commitment to Privacy</h3>
              </div>
              <p>
                Your financial information is deeply personal, and we take your privacy seriously. 
                That's why we built this calculator to run entirely in your browser using JavaScript. 
                When you enter your debt amount, interest rate, or payment information:
              </p>
              <ul className="privacy-points">
                <li>
                  <strong>Nothing is sent to our servers</strong> - All calculations happen on your device
                </li>
                <li>
                  <strong>We can't see your numbers</strong> - Your data never leaves your browser
                </li>
                <li>
                  <strong>No account required</strong> - Use the calculator anonymously
                </li>
                <li>
                  <strong>No tracking of your inputs</strong> - We don't know what you calculate
                </li>
              </ul>
              <p className="privacy-note">
                You can even use this calculator offline if you save the page. That's how committed 
                we are to keeping your financial data private. For full details, see our{' '}
                <button 
                  className="inline-link"
                  onClick={() => {
                    window.location.hash = 'privacy'
                    const element = document.getElementById('privacy')
                    if (element) {
                      const offset = 80
                      const elementPosition = element.getBoundingClientRect().top
                      const offsetPosition = elementPosition + window.pageYOffset - offset
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
                    }
                  }}
                >
                  Privacy Policy
                </button>.
              </p>
            </article>

            {/* Section 4: How It Works */}
            <article className="about-section">
              <div className="section-header">
                <TrendingUp size={28} color="#10b981" />
                <h3>How It Works</h3>
              </div>
              <p>
                Our calculator uses standard financial formulas to show you exactly how long it 
                will take to pay off your debt under different scenarios. Here's what makes it powerful:
              </p>
              <div className="how-it-works-grid">
                <div className="how-it-works-item">
                  <h4>📊 Real-Time Calculations</h4>
                  <p>
                    See instant results as you adjust your payment amount. No need to click "calculate" 
                    or wait—the numbers update immediately.
                  </p>
                </div>
                <div className="how-it-works-item">
                  <h4>💡 Scenario Planning</h4>
                  <p>
                    Experiment with different payment amounts to see how aggressive repayment affects 
                    your timeline and total interest paid.
                  </p>
                </div>
                <div className="how-it-works-item">
                  <h4>📈 Visual Insights</h4>
                  <p>
                    Our payment schedule shows you exactly where your money goes each month—how much 
                    to principal vs. interest.
                  </p>
                </div>
                <div className="how-it-works-item">
                  <h4>🎓 Educational Content</h4>
                  <p>
                    Learn proven strategies like the debt snowball and avalanche methods, plus tips 
                    for staying motivated during your journey.
                  </p>
                </div>
              </div>
              <p className="accuracy-note">
                <strong>Accuracy:</strong> Our calculator uses the same interest calculation methods 
                that credit card companies and banks use. While results are highly accurate, your 
                actual payoff may vary based on factors like fees, rate changes, and payment timing.
              </p>
            </article>

            {/* Section 5: Important Disclaimer */}
            <article className="about-section disclaimer-section">
              <div className="section-header">
                <Scale size={28} color="#ef4444" />
                <h3>Important Disclaimer</h3>
              </div>
              <div className="disclaimer-box">
                <p>
                  <strong>We are not financial advisors</strong>, and this tool does not constitute 
                  financial advice. The calculator provides estimates based on your inputs and standard 
                  interest calculation methods.
                </p>
                <p>
                  Your actual payoff timeline may vary based on factors including:
                </p>
                <ul>
                  <li>Late fees, annual fees, or other charges</li>
                  <li>Changes in your interest rate (variable APR)</li>
                  <li>Payment timing within your billing cycle</li>
                  <li>Additional charges to the account</li>
                  <li>Different calculation methods used by your lender</li>
                </ul>
                <p>
                  <strong>Always verify calculations independently</strong> and consult with a licensed 
                  financial professional or certified credit counselor for personalized advice tailored 
                  to your specific situation.
                </p>
                <p className="disclaimer-resources">
                  <strong>Free resources for professional help:</strong>
                </p>
                <ul className="resource-links">
                  <li>
                    <a 
                      href="https://www.nfcc.org/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      National Foundation for Credit Counseling (NFCC)
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://fcaa.org/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Financial Counseling Association of America (FCAA)
                    </a>
                  </li>
                </ul>
              </div>
            </article>

            {/* Section 6: How We're Funded */}
            <article className="about-section funding-section">
              <div className="section-header">
                <DollarSign size={28} color="#10b981" />
                <h3>How We're Funded</h3>
              </div>
              <p>
                This tool is completely free to use, and we're committed to keeping the core 
                functionality accessible to everyone. We display advertisements through Google AdSense 
                to cover hosting costs, domain registration, and ongoing development.
              </p>
              <div className="funding-commitment">
                <h4>Our Commitment:</h4>
                <ul>
                  <li>✅ The calculator will always be free</li>
                  <li>✅ No paywalls for basic features</li>
                  <li>✅ No selling your data</li>
                  <li>✅ Transparent about how we operate</li>
                </ul>
              </div>
              <p className="future-plans">
                We may introduce optional premium features in the future (like saving multiple scenarios 
                or advanced analytics), but the core debt calculator will remain free forever.
              </p>
            </article>

            {/* Section 7: Get in Touch */}
            <article className="about-section contact-section">
              <div className="section-header">
                <Mail size={28} color="#ff6b35" />
                <h3>Get in Touch</h3>
              </div>
              <p>
                We love hearing from our users! Whether you have questions, suggestions for new features, 
                or just want to share your debt-free success story, we'd be thrilled to hear from you.
              </p>
              <div className="contact-info">
                <div className="contact-method">
                  <Mail size={20} color="#ff6b35" />
                  <div>
                    <h4>Email Us</h4>
                    <p>contact@zerodebtjourney.com</p>
                    <p className="response-time">We typically respond within 24-48 hours</p>
                  </div>
                </div>
              </div>
              <div className="contact-note">
                <p>
                  <strong>Please note:</strong> While we're happy to help with calculator functionality 
                  or answer general questions about the tool, we cannot provide personalized financial 
                  advice. For specific guidance about your financial situation, please consult a licensed 
                  financial professional.
                </p>
              </div>
              <div className="feedback-callout">
                <h4>We're Always Improving</h4>
                <p>
                  Your feedback helps us make this tool better. If you have ideas for new features, 
                  found a bug, or think we can explain something more clearly, please let us know. 
                  This calculator is built for you, and your input shapes its future.
                </p>
              </div>
            </article>

          </div>

          {/* Footer Note */}
          <div className="about-footer">
            <p>
              Thank you for using Zero Debt Journey. We're honored to be part of your path to 
              financial freedom. 🎉
            </p>
          </div>

        </div>

      </div>
    </section>
  )
}

export default AboutPage
